import React, { useState, useEffect, useRef } from 'react';
import { 
  Truck, 
  Search, 
  Filter, 
  CheckCircle, 
  Clock,
  MapPin,
  Eye,
  RefreshCw,
  User,
  Users,
  UserPlus,
  Phone,
  Package,
  Calendar,
  AlertTriangle,
  Navigation,
  TrendingUp,
  BarChart3,
  X,
  Edit,
  Save,
  XCircle,
  Key,
  Star
} from 'lucide-react';
import io from 'socket.io-client';
import { API_BASE_URL, endpoints } from '../../api';
import './AdminDeliveryManagement.css';

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return `$${numeric.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
};

const formatTitleCase = (value) => {
  if (!value) return '';
  const str = String(value).replace(/_/g, ' ').toLowerCase();
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const getDeliveryAddress = (order) => (
  order?.shipping_address ||
  order?.shippingAddress ||
  order?.delivery_address ||
  order?.deliveryAddress ||
  order?.customer_address ||
  order?.address ||
  order?.customer?.shipping_address ||
  order?.customer?.shippingAddress ||
  order?.customer?.delivery_address ||
  order?.customer?.address ||
  order?.user?.address ||
  'N/A'
);

const getVendorDetails = (order) => {
  const vendor = order?.vendor || order?.vendor_details || order?.assigned_vendor || order?.seller || {};
  return {
    name:
      order?.vendor_name ||
      order?.business_name ||
      order?.shop_name ||
      order?.name ||
      vendor?.business_name ||
      vendor?.vendor_name ||
      vendor?.shop_name ||
      vendor?.name ||
      'N/A',
    owner:
      order?.owner_name ||
      order?.vendor_owner ||
      order?.owner ||
      order?.ownerName ||
      order?.contact_person ||
      order?.contactPerson ||
      order?.contact_name ||
      order?.contactName ||
      order?.full_name ||
      order?.fullName ||
      order?.proprietor_name ||
      vendor?.owner_name ||
      vendor?.owner ||
      vendor?.ownerName ||
      vendor?.contact_person ||
      vendor?.contactPerson ||
      vendor?.contact_name ||
      vendor?.contactName ||
      vendor?.full_name ||
      vendor?.fullName ||
      vendor?.proprietor_name ||
      '',
    phone:
      order?.vendor_phone ||
      order?.vendorPhone ||
      order?.vendor_mobile ||
      order?.vendorMobile ||
      order?.owner_phone ||
      order?.ownerPhone ||
      order?.contact_phone ||
      order?.contactPhone ||
      order?.phone ||
      order?.phoneNumber ||
      order?.mobile ||
      order?.mobile_number ||
      order?.phone_number ||
      order?.contact_number ||
      vendor?.phone ||
      vendor?.phoneNumber ||
      vendor?.mobile ||
      vendor?.mobile_number ||
      vendor?.phone_number ||
      vendor?.contact_number ||
      vendor?.contact_phone ||
      vendor?.contactPhone ||
      vendor?.owner_phone ||
      vendor?.ownerPhone ||
      vendor?.vendor_phone ||
      vendor?.vendorPhone ||
      vendor?.vendor_mobile ||
      '',
    email:
      order?.vendor_email ||
      order?.vendorEmail ||
      order?.email ||
      order?.owner_email ||
      order?.contact_email ||
      vendor?.email ||
      vendor?.owner_email ||
      vendor?.contact_email ||
      '',
    address:
      order?.vendor_address ||
      order?.shop_address ||
      order?.business_address ||
      order?.address ||
      vendor?.vendor_address ||
      vendor?.shop_address ||
      vendor?.business_address ||
      vendor?.address ||
      ''
  };
};

const getDeliveryPartnerDetails = (order) => {
  const partner = order?.delivery_partner || order?.partner || order?.assigned_partner || {};
  return {
    id: firstValue(
      order?.delivery_partner_id,
      order?.assigned_partner_id,
      order?.partner_id,
      partner?.id,
      partner?.partner_id,
      ''
    ),
    name: firstValue(
      order?.delivery_partner_name,
      order?.assigned_partner_name,
      order?.partner_name,
      order?.delivery_agent,
      order?.rider_name,
      partner?.full_name,
      partner?.name,
      'N/A'
    ),
    code: firstValue(
      order?.delivery_partner_code,
      order?.partner_code,
      partner?.partner_code,
      'N/A'
    ),
    phone: firstValue(
      order?.delivery_partner_phone,
      order?.partner_phone,
      partner?.phone,
      'N/A'
    )
  };
};

const getOrderVendorId = (order) => String(
  order?.vendor_id ||
  order?.vendorId ||
  order?.vendor?.id ||
  order?.vendor?.vendor_id ||
  order?.vendor?._id ||
  ''
);

const firstValue = (...values) => (
  values.find((value) => {
    if (value === undefined || value === null) return false;
    const normalized = String(value).trim().toLowerCase();
    return normalized !== '' && normalized !== 'n/a' && normalized !== 'na' && normalized !== 'null' && normalized !== 'undefined' && normalized !== '-';
  })
);

const getProductDescription = (item = {}) => firstValue(
  item.product_description,
  item.productDescription,
  item.product_desc,
  item.productDesc,
  item.product_details,
  item.productDetails,
  item.description,
  item.item_description,
  item.itemDescription,
  item.short_description,
  item.shortDescription,
  item.long_description,
  item.longDescription,
  item.meta_description,
  item.metaDescription,
  item.details,
  item.detail,
  item.product?.productDescription,
  item.product?.product_desc,
  item.product?.productDetails,
  item.product?.product_details,
  item.product?.description,
  item.product?.product_description,
  item.product?.shortDescription,
  item.product?.short_description,
  item.product?.longDescription,
  item.product?.long_description,
  item.item?.productDescription,
  item.item?.product_description,
  item.item?.description
) || '';

const normalizeLookupText = (value) => String(value || '').trim().toLowerCase();

const PARTNER_METRIC_KEYS = {
  completedPackages: [
    'derived_completed_packages',
    'completed_packages',
    'packages_completed',
    'completed_package_count',
    'completed_packages_count',
    'delivered_packages',
    'delivered_packages_count',
    'total_packages_delivered',
    'total_delivered_packages',
    'package_deliveries_completed',
    'package_delivery_count',
    'package_count',
    'packages_count',
    'total_packages'
  ],
  completedDeliveries: [
    'derived_completed_deliveries',
    'completed_deliveries',
    'deliveries_completed',
    'completed_delivery_count',
    'completed_deliveries_count',
    'delivered_orders',
    'delivered_orders_count',
    'successful_deliveries',
    'total_completed_deliveries',
    'delivery_count',
    'deliveries_count',
    'total_deliveries'
  ]
};

const readNumericMetric = (sources, keys, fallback = 0) => {
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    for (const key of keys) {
      const value = source[key];
      if (value === undefined || value === null || value === '') continue;
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric;
      }
    }
  }
  return fallback;
};

const findNumericMetricField = (sources, keys) => {
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    for (const key of keys) {
      const value = source[key];
      if (value === undefined || value === null || value === '') continue;
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return { key, value: numeric };
      }
    }
  }
  return null;
};

const normalizePartnerLookupValue = (value) => normalizeLookupText(value);

const getPartnerLookupTokens = (partner = {}, details = {}) => {
  const detailPartner = details?.partner && typeof details.partner === 'object' ? details.partner : {};

  return {
    ids: new Set(
      [
        partner?.id,
        partner?.partner_id,
        details?.id,
        details?.partner_id,
        detailPartner?.id,
        detailPartner?.partner_id
      ]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    ),
    codes: new Set(
      [
        partner?.partner_code,
        partner?.delivery_partner_code,
        details?.partner_code,
        details?.delivery_partner_code,
        detailPartner?.partner_code
      ]
        .map(normalizePartnerLookupValue)
        .filter(Boolean)
    ),
    names: new Set(
      [
        partner?.full_name,
        partner?.name,
        details?.full_name,
        details?.name,
        detailPartner?.full_name,
        detailPartner?.name
      ]
        .map(normalizePartnerLookupValue)
        .filter(Boolean)
    ),
    phones: new Set(
      [
        partner?.phone,
        details?.phone,
        detailPartner?.phone,
        partner?.delivery_partner_phone,
        details?.delivery_partner_phone
      ]
        .map(normalizePartnerLookupValue)
        .filter(Boolean)
    )
  };
};

const packageBelongsToPartner = (item, partnerTokens, options = {}) => {
  const {
    includeGenericFields = true,
    allowFreeTextFallback = true
  } = options;

  const itemIds = [
    item?.delivery_partner_id,
    item?.partner_id,
    item?.assigned_partner_id,
    item?.delivery_partner?.id,
    item?.partner?.id
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  if (itemIds.some((value) => partnerTokens.ids.has(value))) {
    return true;
  }

  const itemCodes = [
    item?.partner_code,
    item?.delivery_partner_code,
    item?.delivery_partner?.partner_code,
    item?.partner?.partner_code
  ]
    .map(normalizePartnerLookupValue)
    .filter(Boolean);

  if (itemCodes.some((value) => partnerTokens.codes.has(value))) {
    return true;
  }

  const itemNames = [
    item?.partner_name,
    item?.delivery_partner_name,
    item?.assigned_partner_name,
    item?.assigned_to,
    item?.delivery_agent,
    item?.rider_name,
    item?.delivery_partner?.full_name,
    item?.partner?.full_name,
    item?.delivery_partner?.name,
    item?.partner?.name
  ];

  if (includeGenericFields) {
    itemNames.push(item?.full_name);
  }

  const normalizedItemNames = itemNames
    .map(normalizePartnerLookupValue)
    .filter(Boolean);

  if (normalizedItemNames.some((value) => partnerTokens.names.has(value))) {
    return true;
  }

  const itemPhones = [
    item?.partner_phone,
    item?.delivery_partner_phone,
    item?.delivery_partner?.phone,
    item?.partner?.phone
  ];

  if (includeGenericFields) {
    itemPhones.push(item?.phone);
  }

  const normalizedItemPhones = itemPhones
    .map(normalizePartnerLookupValue)
    .filter(Boolean);

  if (normalizedItemPhones.some((value) => partnerTokens.phones.has(value))) {
    return true;
  }

  if (!allowFreeTextFallback) {
    return false;
  }

  const freeText = normalizePartnerLookupValue([
    item?.notes,
    item?.delivery_notes,
    item?.special_instructions,
    item?.instructions,
    item?.assigned_to,
    item?.assigned_partner_name,
    item?.delivery_agent,
    item?.rider_name,
    item?.delivery_partner_name,
    item?.partner_name,
    item?.delivery_partner?.full_name,
    item?.partner?.full_name,
    item?.delivery_partner?.name,
    item?.partner?.name,
    item?.delivery_partner?.partner_code,
    item?.partner?.partner_code
  ].filter(Boolean).join(' '));

  if (!freeText) {
    return false;
  }

  const tokenSets = [
    ...partnerTokens.names,
    ...partnerTokens.codes,
    ...partnerTokens.phones
  ].filter(Boolean);

  return tokenSets.some((token) => freeText.includes(token));
};

const parseNumberSafe = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return 0;
};

const getPartnerRecordKey = (item, type = 'package', index = 0) => {
  if (type === 'delivery') {
    return String(
      firstValue(
        item?.id,
        item?.order_id,
        item?.order_number,
        item?.delivery_code,
        item?.tracking_code,
        `delivery-${index}`
      )
    ).trim();
  }

  return String(
    firstValue(
      item?.id,
      item?.package_id,
      item?.delivery_code,
      item?.tracking_code,
      item?.order_number,
      `package-${index}`
    )
  ).trim();
};

const dedupePartnerRecords = (items = [], type = 'package') => {
  const uniqueItems = new Map();

  items.forEach((item, index) => {
    const key = getPartnerRecordKey(item, type, index);
    if (!key || uniqueItems.has(key)) {
      return;
    }
    uniqueItems.set(key, item);
  });

  return Array.from(uniqueItems.values());
};

const buildRecentCompletedDeliveryFallback = (recentDeliveries = []) => dedupePartnerRecords(
  recentDeliveries
    .filter((item) => normalizeLookupText(item?.delivery_status || item?.status) === 'delivered')
    .map((item, index) => ({
      ...item,
      id: firstValue(item?.id, item?.order_id, `recent-delivery-${index}`),
      order_number: firstValue(item?.order_number, item?.delivery_code, item?.tracking_code, item?.id, `Order ${index + 1}`),
      customer_name: firstValue(item?.customer_name, item?.full_name, 'N/A'),
      customer_phone: firstValue(item?.customer_phone, item?.phone, 'N/A'),
      shipping_address: firstValue(
        item?.shipping_address,
        item?.delivery_address,
        item?.customer_address,
        item?.drop_address,
        item?.destination_address,
        'N/A'
      ),
      total_amount: parseNumberSafe(item?.total_amount, item?.partner_earnings, item?.amount, 0),
      delivered_at: firstValue(item?.delivered_at, item?.updated_at, item?.created_at, '')
    })),
  'delivery'
);

const renderRatingStars = (value) => {
  const rating = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));
  return Array.from({ length: 5 }, (_, index) => (
    <Star
      key={index}
      size={15}
      fill={index < rating ? 'currentColor' : 'none'}
      strokeWidth={index < rating ? 0 : 2}
    />
  ));
};

const fetchAdminOrderDetail = async (orderId, apiKey) => {
  const res = await fetch(`${API_BASE_URL}${endpoints.adminOrderById(orderId)}`, {
    headers: { 'x-admin-key': apiKey }
  });

  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || `Failed to load order ${orderId}`);
  }

  const data = await res.json().catch(() => ({}));
  return data?.order || data?.data?.order || data?.data || data || {};
};

const extractCollectionItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.orders)) return payload.orders;
  if (Array.isArray(payload?.packages)) return payload.packages;
  if (Array.isArray(payload?.partners)) return payload.partners;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const extractCollectionPagination = (payload) => (
  payload?.pagination ||
  payload?.data?.pagination ||
  null
);

const fetchAllAdminPages = async (path, apiKey, extraParams = {}) => {
  const aggregated = [];
  let currentPage = 1;
  let totalPages = 1;

  do {
    const params = new URLSearchParams();
    params.set('page', String(currentPage));
    params.set('limit', '5000');

    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    });

    const separator = path.includes('?') ? '&' : '?';
    const res = await fetch(`${API_BASE_URL}${path}${separator}${params.toString()}`, {
      headers: { 'x-admin-key': apiKey }
    });

    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.error || 'Failed to load paginated records');
    }

    const data = await res.json().catch(() => ({}));
    const items = extractCollectionItems(data);
    aggregated.push(...items);

    const pagination = extractCollectionPagination(data);
    const nextTotalPages = Number(
      pagination?.total_pages ||
      pagination?.pages ||
      pagination?.last_page ||
      1
    );

    if (Number.isFinite(nextTotalPages) && nextTotalPages > 0) {
      totalPages = nextTotalPages;
    } else if (items.length < 5000) {
      totalPages = currentPage;
    } else {
      totalPages = currentPage + 1;
    }

    currentPage += 1;
  } while (currentPage <= totalPages);

  return aggregated;
};

const getPartnerMetricSummary = (partner = {}, details = {}) => {
  const detailPartner = details?.partner && typeof details.partner === 'object' ? details.partner : {};
  const detailStats = details?.stats && typeof details.stats === 'object' ? details.stats : {};
  const detailPerformance = details?.performance && typeof details.performance === 'object' ? details.performance : {};
  const sources = [details, detailPartner, detailStats, detailPerformance, partner];
  const packagesSource = findNumericMetricField(sources, PARTNER_METRIC_KEYS.completedPackages);
  const deliveriesSource = findNumericMetricField(sources, PARTNER_METRIC_KEYS.completedDeliveries);
  const completedPackagesField = packagesSource?.key || (details?.derived_completed_packages_source ? `derived:${details.derived_completed_packages_source}` : 'not found');
  const completedDeliveriesField = deliveriesSource?.key || (
    details?.derived_completed_deliveries_source
      ? `derived:${details.derived_completed_deliveries_source}`
      : (partner?.total_deliveries !== undefined ? 'total_deliveries' : 'not found')
  );

  return {
    completedPackages: packagesSource?.value ?? 0,
    completedDeliveries: deliveriesSource?.value ?? (Number(partner?.total_deliveries) || 0),
    completedPackagesField,
    completedDeliveriesField
  };
};

const AdminDeliveryManagement = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [selectedVendorFilter, setSelectedVendorFilter] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDetailsFor, setShowDetailsFor] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});
  const [socket, setSocket] = useState(null);
  const [stats, setStats] = useState({
    totalDeliveries: 0,
    outForDelivery: 0,
    delivered: 0,
    pending: 0,
    averageDeliveryTime: 0
  });
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [deliveryAgent, setDeliveryAgent] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const socketRef = useRef(null);
  const [vendors, setVendors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [paginationInfo, setPaginationInfo] = useState(null);

  // Delivery partners state
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnerPage, setPartnerPage] = useState(1);
  const [partnerLimit, setPartnerLimit] = useState(20);
  const [partnerStatusFilter, setPartnerStatusFilter] = useState('');
  const [partnerVerificationFilter, setPartnerVerificationFilter] = useState('');
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerPagination, setPartnerPagination] = useState(null);
  const [showCreatePartnerModal, setShowCreatePartnerModal] = useState(false);
  const [creatingPartner, setCreatingPartner] = useState(false);
  const [selectedPartnerDetails, setSelectedPartnerDetails] = useState(null);
  const [partnerDetailsLoading, setPartnerDetailsLoading] = useState(false);
  const [createPartnerForm, setCreatePartnerForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    aadhaar_number: '',
    pan_number: '',
    license_number: '',
    license_expiry_date: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_account_holder_name: '',
    upi_id: ''
  });
  const [partnerActionLoadingId, setPartnerActionLoadingId] = useState(null);
  const [partnerToast, setPartnerToast] = useState({ visible: false, text: '', type: 'success' });

  // Order → Delivery Partner assignment state
  const [assignOrder, setAssignOrder] = useState(null);
  const [assignPartnerId, setAssignPartnerId] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);

  const deliveryVisibleStatuses = [
    'pending',
    'placed',
    'confirmed',
    'packing',
    'ready',
    'out_for_delivery',
    'delivered'
  ];

  const normalizeDeliveryStatus = (status) => {
    const normalized = String(status || '').toLowerCase().trim();
    return normalized === 'pending' ? 'placed' : normalized;
  };

  // Fetch delivery queue orders, including older pre-dispatch orders.
  const fetchDeliveries = async (pageParam = currentPage) => {
    try {
      setLoading(true);
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const params = new URLSearchParams();
      
      if (filter !== 'all') {
        params.append('status', filter);
      }
      
      if (selectedVendorFilter.length === 1) params.append('vendor_id', selectedVendorFilter[0]);
      if (searchQuery) params.append('search', searchQuery);
      if (dateFrom && dateTo) { 
        params.append('date_from', dateFrom); 
        params.append('date_to', dateTo); 
      }
      
      const pageToFetch = typeof pageParam === 'number' ? pageParam : 1;
      params.append('page', String(pageToFetch));
      params.append('limit', String(selectedVendorFilter.length > 1 ? 5000 : rowsPerPage));

      const response = await fetch(`${API_BASE_URL}/api/admin/orders?${params.toString()}`, {
        headers: {
          'x-admin-key': apiKey
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.orders)
            ? data.orders
            : [];
        
        const selectedVendorSet = new Set(selectedVendorFilter.map(String));
        const allMatchingDeliveries = list
          .map(order => ({
            ...order,
            status: normalizeDeliveryStatus(order.status)
          }))
          .filter(order => deliveryVisibleStatuses.includes(order.status))
          .filter(order => (
            selectedVendorSet.size === 0 ||
            selectedVendorSet.has(String(
              order.vendor_id ||
              order.vendorId ||
              order.vendor?.id ||
              order.vendor?.vendor_id ||
              order.vendor?._id ||
              ''
            ))
          ));
        const deliveryOrders = selectedVendorFilter.length > 1
          ? allMatchingDeliveries.slice((pageToFetch - 1) * rowsPerPage, pageToFetch * rowsPerPage)
          : allMatchingDeliveries;
        
        setDeliveries(deliveryOrders);
        
        if (selectedVendorFilter.length > 1) {
          setPaginationInfo({
            current_page: pageToFetch,
            total_pages: Math.ceil(allMatchingDeliveries.length / rowsPerPage),
            total_orders: allMatchingDeliveries.length
          });
          setCurrentPage(pageToFetch);
        } else if (data?.pagination) {
          setPaginationInfo(data.pagination);
          const serverPage = parseInt(data.pagination.current_page, 10);
          if (!Number.isNaN(serverPage) && serverPage > 0) {
            setCurrentPage(serverPage);
          }
        }
        
        // Calculate stats
        calculateStats(deliveryOrders);
      }
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (orders) => {
    const total = orders.length;
    const outForDelivery = orders.filter(o => o.status === 'out_for_delivery').length;
    const delivered = orders.filter(o => o.status === 'delivered').length;
    const pending = orders.filter(o => ['placed', 'confirmed', 'packing', 'ready'].includes(o.status)).length;
    
    // Calculate average delivery time (if delivered_at and out_for_delivery_at exist)
    let totalDeliveryTime = 0;
    let deliveryCount = 0;
    orders.forEach(order => {
      if (order.delivered_at && order.out_for_delivery_at) {
        const delivered = new Date(order.delivered_at).getTime();
        const outForDelivery = new Date(order.out_for_delivery_at).getTime();
        if (!Number.isNaN(delivered) && !Number.isNaN(outForDelivery)) {
          totalDeliveryTime += (delivered - outForDelivery) / (1000 * 60); // minutes
          deliveryCount++;
        }
      }
    });
    
    const averageDeliveryTime = deliveryCount > 0 
      ? Math.round(totalDeliveryTime / deliveryCount) 
      : 0;

    setStats({
      totalDeliveries: total,
      outForDelivery,
      delivered,
      pending,
      averageDeliveryTime
    });
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const res = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}`, {
        headers: { 'x-admin-key': apiKey }
      });
      if (res.ok) {
        const data = await res.json();
        const details = data.order || data.data?.order || data.data || data;
        const existingDelivery = deliveries.find(order => String(order.id) === String(orderId)) || {};
        const existingVendor = existingDelivery.vendor || existingDelivery.vendor_details || {};
        const detailsVendor = details.vendor || details.vendor_details || {};
        const mergedDetails = {
          ...existingDelivery,
          ...details,
          vendor_id: firstValue(details.vendor_id, details.vendorId, existingDelivery.vendor_id, existingDelivery.vendorId, detailsVendor.id, detailsVendor.vendor_id, existingVendor.id, existingVendor.vendor_id),
          vendor_name: firstValue(details.vendor_name, details.business_name, details.shop_name, existingDelivery.vendor_name, existingDelivery.business_name, existingDelivery.shop_name, detailsVendor.vendor_name, detailsVendor.business_name, detailsVendor.shop_name, existingVendor.vendor_name, existingVendor.business_name, existingVendor.shop_name),
          business_name: firstValue(details.business_name, existingDelivery.business_name, detailsVendor.business_name, existingVendor.business_name),
          shop_name: firstValue(details.shop_name, existingDelivery.shop_name, detailsVendor.shop_name, existingVendor.shop_name),
          owner_name: firstValue(details.owner_name, details.ownerName, details.owner, details.vendor_owner, details.contact_person, details.contact_name, details.full_name, existingDelivery.owner_name, existingDelivery.ownerName, existingDelivery.owner, existingDelivery.vendor_owner, existingDelivery.contact_person, existingDelivery.contact_name, existingDelivery.full_name, detailsVendor.owner_name, detailsVendor.ownerName, detailsVendor.owner, detailsVendor.contact_person, detailsVendor.contact_name, detailsVendor.full_name, existingVendor.owner_name, existingVendor.ownerName, existingVendor.owner, existingVendor.contact_person, existingVendor.contact_name, existingVendor.full_name),
          vendor_phone: firstValue(details.vendor_phone, details.vendorPhone, details.vendor_mobile, details.owner_phone, details.contact_phone, details.phone, details.phoneNumber, details.mobile, details.mobile_number, existingDelivery.vendor_phone, existingDelivery.vendorPhone, existingDelivery.vendor_mobile, existingDelivery.owner_phone, existingDelivery.contact_phone, existingDelivery.phone, existingDelivery.phoneNumber, existingDelivery.mobile, existingDelivery.mobile_number, detailsVendor.vendor_phone, detailsVendor.vendorPhone, detailsVendor.vendor_mobile, detailsVendor.owner_phone, detailsVendor.contact_phone, detailsVendor.phone, detailsVendor.phoneNumber, detailsVendor.mobile, detailsVendor.mobile_number, existingVendor.vendor_phone, existingVendor.vendorPhone, existingVendor.vendor_mobile, existingVendor.owner_phone, existingVendor.contact_phone, existingVendor.phone, existingVendor.phoneNumber, existingVendor.mobile, existingVendor.mobile_number),
          vendor_email: firstValue(details.vendor_email, details.vendorEmail, details.email, existingDelivery.vendor_email, existingDelivery.vendorEmail, existingDelivery.email, detailsVendor.email, existingVendor.email),
          vendor_address: firstValue(details.vendor_address, details.shop_address, details.business_address, existingDelivery.vendor_address, existingDelivery.shop_address, existingDelivery.business_address, detailsVendor.vendor_address, detailsVendor.shop_address, detailsVendor.business_address, existingVendor.vendor_address, existingVendor.shop_address, existingVendor.business_address),
          vendor: Object.keys(detailsVendor).length > 0 ? detailsVendor : existingVendor
        };
        setOrderDetails(prev => ({ ...prev, [orderId]: mergedDetails }));
      }
    } catch (e) {
      console.error('Error fetching order details:', e);
    }
  };

  const fetchVendors = async () => {
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const response = await fetch(`${API_BASE_URL}/api/admin/vendors?limit=5000`, {
        headers: {
          'x-admin-key': apiKey
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const vendors = data.items || data.vendors || (Array.isArray(data) ? data : []);
        setVendors(vendors);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const getVendorOptionLabel = (vendor) => (
    vendor.business_name ||
    vendor.vendor_name ||
    vendor.shop_name ||
    vendor.owner_name ||
    vendor.email ||
    `Vendor #${vendor.id}`
  );

  const getVendorOptionValue = (vendor) => String(
    vendor.id ??
    vendor.vendor_id ??
    vendor.vendorId ??
    vendor._id ??
    ''
  );

  const findVendorRecordForOrder = (order) => {
    const orderVendorId = getOrderVendorId(order);
    if (orderVendorId) {
      const vendorById = vendors.find(vendor => getVendorOptionValue(vendor) === orderVendorId);
      if (vendorById) return vendorById;
    }

    const orderVendorName = normalizeLookupText(
      order?.vendor_name ||
      order?.business_name ||
      order?.shop_name ||
      order?.vendor?.business_name ||
      order?.vendor?.vendor_name ||
      order?.vendor?.shop_name
    );

    if (!orderVendorName) return null;

    return vendors.find((vendor) => {
      const candidateNames = [
        vendor.business_name,
        vendor.vendor_name,
        vendor.shop_name,
        vendor.name
      ].map(normalizeLookupText);
      return candidateNames.includes(orderVendorName);
    }) || null;
  };

  const getResolvedVendorDetails = (order) => {
    const vendorRecord = findVendorRecordForOrder(order);
    return getVendorDetails({
      ...(vendorRecord || {}),
      ...order,
      vendor: order?.vendor || order?.vendor_details || vendorRecord
    });
  };

  const getResolvedDeliveryPartnerDetails = (order) => {
    const baseDetails = getDeliveryPartnerDetails(order);
    const partnerId = String(baseDetails.id || '').trim();
    const partnerRecord = partnerId
      ? partners.find((partner) => String(partner.id || partner.partner_id || '') === partnerId)
      : partners.find((partner) => {
          const candidateNames = [
            partner.full_name,
            partner.name
          ].map(normalizeLookupText);
          const candidateCodes = [
            partner.partner_code
          ].map(normalizeLookupText);
          return (
            candidateNames.includes(normalizeLookupText(baseDetails.name)) ||
            candidateCodes.includes(normalizeLookupText(baseDetails.code))
          );
        });

    if (!partnerRecord) {
      return baseDetails;
    }

    return {
      id: firstValue(baseDetails.id, partnerRecord.id, partnerRecord.partner_id, ''),
      name: firstValue(baseDetails.name, partnerRecord.full_name, partnerRecord.name, 'N/A'),
      code: firstValue(baseDetails.code, partnerRecord.partner_code, 'N/A'),
      phone: firstValue(baseDetails.phone, partnerRecord.phone, 'N/A')
    };
  };

  const getVendorFilterLabel = () => {
    if (selectedVendorFilter.length === 0) return 'All Vendors';
    if (selectedVendorFilter.length === 1) {
      const selectedVendor = vendors.find(vendor => getVendorOptionValue(vendor) === selectedVendorFilter[0]);
      return selectedVendor ? getVendorOptionLabel(selectedVendor) : '1 vendor selected';
    }
    return `${selectedVendorFilter.length} vendors selected`;
  };

  const toggleVendorFilter = (vendorId) => {
    const vendorKey = String(vendorId || '');
    if (!vendorKey) return;
    setSelectedVendorFilter(prev => (
      prev.includes(vendorKey)
        ? prev.filter(id => id !== vendorKey)
        : [...prev, vendorKey]
    ));
  };

  // WebSocket connection for real-time updates
  useEffect(() => {
    const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
    if (apiKey) {
      const socketUrl = API_BASE_URL || window.location.origin;
      const newSocket = io(socketUrl, {
        auth: { token: apiKey, type: 'admin' }
      });
      
      newSocket.on('connect', () => {
        console.log('Admin connected to WebSocket for deliveries');
        newSocket.emit('join-admin');
      });
      
      newSocket.on('order-update', (orderData) => {
        const nextStatus = normalizeDeliveryStatus(orderData.status);
        if (deliveryVisibleStatuses.includes(nextStatus)) {
          const normalizedOrderData = { ...orderData, status: nextStatus };
          setDeliveries(prev => {
            const exists = prev.some(o => o.id === normalizedOrderData.id);
            if (exists) {
              return prev.map(order => order.id === normalizedOrderData.id ? { ...order, ...normalizedOrderData } : order);
            }
            return [normalizedOrderData, ...prev];
          });
          fetchDeliveries(currentPage);
        }
      });
      
      setSocket(newSocket);
      socketRef.current = newSocket;
    }
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Polling fallback
  useEffect(() => {
    const interval = setInterval(() => {
      const connected = !!socketRef.current && socketRef.current.connected;
      if (!connected) {
        fetchDeliveries(currentPage);
      }
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, [currentPage]);

  useEffect(() => {
    fetchDeliveries(1);
    fetchVendors();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchDeliveries(1);
  }, [filter, selectedVendorFilter, dateFrom, dateTo]);

  // Helper for partner toast
  const showPartnerToast = (text, type = 'success', timeoutMs = 2500) => {
    setPartnerToast({ text, type, visible: true });
    window.clearTimeout(showPartnerToast._t);
    showPartnerToast._t = window.setTimeout(
      () => setPartnerToast(prev => ({ ...prev, visible: false })),
      timeoutMs
    );
  };

  // Fetch delivery partners list
  const fetchPartners = async (pageParam = partnerPage, limitParam = partnerLimit) => {
    try {
      setPartnersLoading(true);
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const qs = new URLSearchParams();
      qs.set('page', String(pageParam));
      qs.set('limit', String(limitParam));
      if (partnerStatusFilter) qs.set('status', partnerStatusFilter);
      if (partnerVerificationFilter) qs.set('verification_status', partnerVerificationFilter);
      if (partnerSearch) qs.set('search', partnerSearch);

      const res = await fetch(
        `${API_BASE_URL}${endpoints.adminDeliveryPartners}?${qs.toString()}`,
        {
          headers: { 'x-admin-key': apiKey }
        }
      );

      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Failed to load delivery partners');
      }

      const data = await res.json();
      setPartners(data.partners || []);
      setPartnerPagination(data.pagination || null);
      setPartnerPage(data.pagination?.page || pageParam);
      setPartnerLimit(data.pagination?.limit || limitParam);
    } catch (e) {
      console.error('Error fetching delivery partners:', e);
      showPartnerToast(e.message || 'Failed to load partners', 'error');
    } finally {
      setPartnersLoading(false);
    }
  };

  useEffect(() => {
    // load partners initially
    fetchPartners(1, partnerLimit);
  }, []);

  useEffect(() => {
    // refetch when filters change
    setPartnerPage(1);
    fetchPartners(1, partnerLimit);
  }, [partnerStatusFilter, partnerVerificationFilter]);

  const handleCreatePartnerChange = (e) => {
    const { name, value } = e.target;
    setCreatePartnerForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreatePartner = async () => {
    if (!createPartnerForm.full_name || !createPartnerForm.email || !createPartnerForm.phone) {
      showPartnerToast('Full name, email and phone are required', 'error');
      return;
    }
    try {
      setCreatingPartner(true);
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const res = await fetch(`${API_BASE_URL}${endpoints.adminDeliveryPartnerCreate}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': apiKey
        },
        body: JSON.stringify(createPartnerForm)
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Failed to create delivery partner');
      }
      const data = await res.json();
      showPartnerToast('Delivery partner created successfully', 'success');
      setShowCreatePartnerModal(false);
      setCreatePartnerForm({
        full_name: '',
        email: '',
        phone: '',
        password: '',
        aadhaar_number: '',
        pan_number: '',
        license_number: '',
        license_expiry_date: '',
        bank_account_number: '',
        bank_ifsc: '',
        bank_account_holder_name: '',
        upi_id: ''
      });
      // Refresh list
      fetchPartners(1, partnerLimit);
      if (data.temp_password) {
        // surface generated password
        alert(`Temporary password for ${data.partner?.full_name || 'partner'}: ${data.temp_password}`);
      }
    } catch (e) {
      console.error('Create partner error:', e);
      showPartnerToast(e.message || 'Failed to create delivery partner', 'error');
    } finally {
      setCreatingPartner(false);
    }
  };

  const fetchPartnerDetails = async (partner) => {
    if (!partner?.id) return;

    try {
      setPartnerDetailsLoading(true);
      setSelectedPartnerDetails({
        partner,
        details: null
      });

      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const res = await fetch(`${API_BASE_URL}${endpoints.adminDeliveryPartnerById(partner.id)}`, {
        headers: { 'x-admin-key': apiKey }
      });

      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Failed to load partner details');
      }

      const data = await res.json().catch(() => ({}));
      const details = data?.partner || data?.data?.partner || data?.data || data || {};
      const partnerTokens = getPartnerLookupTokens(partner, details);
      let derivedCompletedPackages = null;
      let derivedCompletedDeliveries = null;
      let completedPackageOrders = [];
      let completedDeliveryOrders = [];

      try {
        const packageList = await fetchAllAdminPages(endpoints.packageOrders, apiKey);
        const deliveredPackages = dedupePartnerRecords(packageList.filter((item) => {
          const status = normalizeLookupText(
            firstValue(item?.delivery_status, item?.status, item?.shipment_status)
          );
          return status === 'delivered' && packageBelongsToPartner(item, partnerTokens);
        }), 'package');
        completedPackageOrders = deliveredPackages;

        derivedCompletedPackages = deliveredPackages.reduce(
          (sum, item) => sum + Math.max(1, parseNumberSafe(item?.package_count, item?.packages_count, 1)),
          0
        );
      } catch (deriveError) {
        console.error('Partner package metrics derive error:', deriveError);
      }

      try {
        const recentDeliveries = Array.isArray(details?.recent_deliveries)
          ? details.recent_deliveries.filter((item) => normalizeLookupText(item?.delivery_status || item?.status) === 'delivered')
          : [];

        if (recentDeliveries.length > 0) {
          const orderDetails = await Promise.all(
            recentDeliveries.map(async (item) => {
              try {
                const orderDetailsItem = await fetchAdminOrderDetail(item.id, apiKey);
                return {
                  ...item,
                  ...orderDetailsItem,
                  id: firstValue(orderDetailsItem?.id, item?.id),
                  order_number: firstValue(orderDetailsItem?.order_number, item?.delivery_code, item?.id),
                  delivered_at: firstValue(orderDetailsItem?.delivered_at, item?.delivered_at, item?.updated_at, item?.created_at),
                  total_amount: parseNumberSafe(orderDetailsItem?.total_amount, item?.partner_earnings, orderDetailsItem?.amount),
                  delivery_code: firstValue(item?.delivery_code, orderDetailsItem?.delivery_code)
                };
              } catch (orderError) {
                console.error(`Partner order detail fetch error for ${item?.id}:`, orderError);
                return {
                  ...item,
                  order_number: firstValue(item?.order_number, item?.delivery_code, item?.id),
                  customer_name: firstValue(item?.customer_name, 'N/A'),
                  customer_phone: firstValue(item?.customer_phone, 'N/A'),
                  shipping_address: firstValue(item?.shipping_address, item?.delivery_address, item?.customer_address, 'N/A'),
                  total_amount: parseNumberSafe(item?.total_amount, item?.partner_earnings, 0),
                  delivered_at: firstValue(item?.delivered_at, item?.updated_at, item?.created_at, '')
                };
              }
            })
          );

          completedDeliveryOrders = dedupePartnerRecords(orderDetails, 'delivery');
        } else {
          const deliveredOrders = await fetchAllAdminPages('/api/admin/orders', apiKey, { status: 'delivered' });
          completedDeliveryOrders = dedupePartnerRecords(
            deliveredOrders.filter((item) => packageBelongsToPartner(item, partnerTokens, {
              includeGenericFields: false,
              allowFreeTextFallback: false
            })),
            'delivery'
          );
        }

        derivedCompletedDeliveries = completedDeliveryOrders.length;
      } catch (deriveError) {
        console.error('Partner delivery metrics derive error:', deriveError);
      }

      setSelectedPartnerDetails({
        partner,
        details: {
          ...details,
          ...(derivedCompletedPackages !== null
            ? {
                derived_completed_packages: derivedCompletedPackages,
                derived_completed_packages_source: 'package-orders',
                completed_package_orders: completedPackageOrders
              }
            : {}),
          ...(derivedCompletedDeliveries !== null
            ? {
                derived_completed_deliveries: derivedCompletedDeliveries,
                derived_completed_deliveries_source: 'orders:delivered',
                completed_delivery_orders: completedDeliveryOrders
              }
            : {})
        }
      });
    } catch (e) {
      console.error('Partner details error:', e);
      showPartnerToast(e.message || 'Failed to load partner details', 'error');
      setSelectedPartnerDetails({
        partner,
        details: null
      });
    } finally {
      setPartnerDetailsLoading(false);
    }
  };

  const performPartnerAction = async (id, type, extraBody = {}) => {
    try {
      setPartnerActionLoadingId(id);
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      let url;
      let method = 'PUT';
      let body = {};

      if (type === 'approve') {
        url = `${API_BASE_URL}${endpoints.adminDeliveryPartnerApprove(id)}`;
        body = { verification_notes: extraBody.notes || '' };
      } else if (type === 'reject') {
        url = `${API_BASE_URL}${endpoints.adminDeliveryPartnerReject(id)}`;
        body = { verification_notes: extraBody.notes || 'Rejected by admin' };
      } else if (type === 'block' || type === 'unblock') {
        url = `${API_BASE_URL}${endpoints.adminDeliveryPartnerBlock(id)}`;
        body = { action: type };
      } else if (type === 'resetPassword') {
        url = `${API_BASE_URL}${endpoints.adminDeliveryPartnerResetPassword(id)}`;
        method = 'PUT';
        body = extraBody.new_password ? { new_password: extraBody.new_password } : {};
      } else {
        return;
      }

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': apiKey
        },
        body: Object.keys(body).length ? JSON.stringify(body) : undefined
      });

      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Action failed');
      }

      const data = await res.json().catch(() => ({}));

      if (type === 'resetPassword' && data.temp_password) {
        alert(`Temporary password: ${data.temp_password}`);
      }

      showPartnerToast('Action completed', 'success');
      fetchPartners(partnerPage, partnerLimit);
    } catch (e) {
      console.error('Partner action error:', e);
      showPartnerToast(e.message || 'Action failed', 'error');
    } finally {
      setPartnerActionLoadingId(null);
    }
  };

  // Update delivery status
  const updateDeliveryStatus = async (orderId, status) => {
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'x-admin-key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        fetchDeliveries(currentPage);
        setEditingDelivery(null);
        setDeliveryAgent('');
        setDeliveryNotes('');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update delivery status');
      }
    } catch (error) {
      console.error('Error updating delivery status:', error);
      alert('Failed to update delivery status');
    }
  };

  const handleAssignDelivery = async (order) => {
    if (!deliveryAgent.trim()) {
      alert('Please enter delivery agent name');
      return;
    }

    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      // First update status to out_for_delivery
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${order.id}/status`, {
        method: 'PUT',
        headers: {
          'x-admin-key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          status: 'out_for_delivery',
          notes: `Assigned to: ${deliveryAgent}. ${deliveryNotes || ''}`
        })
      });
      
      if (response.ok) {
        fetchDeliveries(currentPage);
        setEditingDelivery(null);
        setDeliveryAgent('');
        setDeliveryNotes('');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to assign delivery');
      }
    } catch (error) {
      console.error('Error assigning delivery:', error);
      alert('Failed to assign delivery');
    }
  };

   // Assign / Reassign delivery partner to order
   const handleSaveOrderAssignment = async () => {
     if (!assignOrder || !assignPartnerId) return;
     try {
       setAssignSaving(true);
       const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
       const response = await fetch(
         `${API_BASE_URL}/api/admin/delivery/order/${assignOrder.id}/assign`,
         {
           method: 'POST',
           headers: {
             'x-admin-key': apiKey,
             'Content-Type': 'application/json'
           },
           body: JSON.stringify({ delivery_partner_id: Number(assignPartnerId) })
         }
       );

       if (!response.ok) {
         const error = await response.json().catch(() => ({}));
         alert(error.error || error.message || 'Failed to assign delivery partner');
       } else {
         const data = await response.json().catch(() => ({}));
         // Optional: surface success message
         if (data?.message) {
           console.log('Assignment success:', data.message);
         }
         // Refresh deliveries so status reflects assignment
         fetchDeliveries(currentPage);
         setAssignOrder(null);
         setAssignPartnerId('');
       }
     } catch (e) {
       console.error('Error assigning delivery partner:', e);
       alert('Failed to assign delivery partner');
     } finally {
       setAssignSaving(false);
     }
   };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'placed': 'status-placed',
      'confirmed': 'status-confirmed',
      'packing': 'status-packing',
      'ready': 'status-ready',
      'out_for_delivery': 'status-out-for-delivery',
      'delivered': 'status-delivered'
    };
    return statusClasses[status] || 'status-default';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'placed': return <Clock size={16} />;
      case 'confirmed': return <CheckCircle size={16} />;
      case 'packing': return <Package size={16} />;
      case 'ready': return <Package size={16} />;
      case 'out_for_delivery': return <Truck size={16} />;
      case 'delivered': return <CheckCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusText = (status) => {
    const statusTexts = {
      'placed': 'Placed',
      'confirmed': 'Confirmed',
      'packing': 'Packing',
      'ready': 'Ready for Delivery',
      'out_for_delivery': 'Out for Delivery',
      'delivered': 'Delivered'
    };
    return statusTexts[status] || status;
  };

  const filteredDeliveries = deliveries.filter(delivery => {
    const matchesSearch = delivery.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (delivery.customer_name && delivery.customer_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         (delivery.shipping_address && delivery.shipping_address.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = filter === 'all' || delivery.status === filter;
    return matchesSearch && matchesFilter;
  });

  const readyDeliveries = deliveries.filter(d => ['placed', 'confirmed', 'packing', 'ready'].includes(d.status));
  const activeDeliveries = deliveries.filter(d => d.status === 'out_for_delivery');
  const completedDeliveries = deliveries.filter(d => d.status === 'delivered');

  // Only show partners that are active & verified for assignment
  const assignablePartners = partners.filter(
    (p) => p.status === 'active' && p.verification_status === 'verified'
  );
  
  const visibleDeliveries = activeTab === 'ready'
    ? readyDeliveries
    : activeTab === 'active'
      ? activeDeliveries
      : activeTab === 'completed'
        ? completedDeliveries
        : filteredDeliveries;

  const totalDeliveries = paginationInfo?.total_orders ?? deliveries.length;
  const totalPages = paginationInfo?.total_pages ?? (deliveries.length > 0 ? 1 : 0);

  const partnerTotal = partnerPagination?.total ?? partners.length;
  const partnerTotalPages = partnerPagination?.totalPages ?? (partners.length > 0 ? 1 : 0);

  return (
    <div className="admin-delivery-management">
      <div className="admin-delivery-header">
        <div className="header-content">
          <h1>
            <Truck size={32} style={{ marginRight: 12 }} />
            Delivery Management
          </h1>
          <p>Track and manage all deliveries across vendors with real-time updates</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => fetchDeliveries(currentPage)}>
            <RefreshCw size={20} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="delivery-stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#3b82f6' }}>
            <Truck size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalDeliveries}</div>
            <div className="stat-label">Total Deliveries</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f59e0b' }}>
            <Package size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-label">Pending / Ready</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#10b981' }}>
            <Navigation size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.outForDelivery}</div>
            <div className="stat-label">Out for Delivery</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#8b5cf6' }}>
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.delivered}</div>
            <div className="stat-label">Delivered</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#06b6d4' }}>
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.averageDeliveryTime}m</div>
            <div className="stat-label">Avg Delivery Time</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-delivery-tabs">
        <button 
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <Eye size={16} />
          All Deliveries ({deliveries.length})
        </button>
        <button 
          className={`tab ${activeTab === 'ready' ? 'active' : ''}`}
          onClick={() => setActiveTab('ready')}
        >
          <Package size={16} />
          Pending / Ready ({readyDeliveries.length})
        </button>
        <button 
          className={`tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          <Truck size={16} />
          In Transit ({activeDeliveries.length})
        </button>
        <button 
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          <CheckCircle size={16} />
          Completed ({completedDeliveries.length})
        </button>
      </div>

      {/* Filters */}
      <div className="admin-delivery-filters">
        <div className="filters-left">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search by order number, customer, address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setCurrentPage(1);
                  fetchDeliveries(1);
                }
              }}
              className="search-input"
            />
          </div>
          
          <div className="filter-group">
            <Filter size={20} />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="placed">Placed</option>
              <option value="confirmed">Confirmed</option>
              <option value="packing">Packing</option>
              <option value="ready">Ready</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>

          <div className="filter-group">
            <Filter size={20} />
            <div className="vendor-multi-select">
              <button
                type="button"
                className="vendor-multi-trigger"
                onClick={() => setShowVendorDropdown(prev => !prev)}
              >
                {getVendorFilterLabel()}
              </button>
              {showVendorDropdown && (
                <div className="vendor-multi-menu">
                  <label className="vendor-option">
                    <input
                      type="checkbox"
                      checked={selectedVendorFilter.length === 0}
                      onChange={() => setSelectedVendorFilter([])}
                    />
                    <span>All Vendors</span>
                  </label>
                  {vendors.map(v => {
                    const vendorId = getVendorOptionValue(v);
                    if (!vendorId) return null;
                    return (
                      <button
                        key={vendorId}
                        type="button"
                        className="vendor-option"
                        onClick={() => toggleVendorFilter(vendorId)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedVendorFilter.includes(vendorId)}
                          readOnly
                        />
                        <span>{getVendorOptionLabel(v)}</span>
                      </button>
                    );
                  })}
                  {vendors.length === 0 && (
                    <div className="vendor-option empty">No vendors found</div>
                  )}
                </div>
              )}
              {selectedVendorFilter.length > 0 && (
                <button
                  type="button"
                  className="clear-vendor-filter"
                  onClick={() => setSelectedVendorFilter([])}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="filters-right">
          <div className="date-range">
            <input 
              type="date" 
              value={dateFrom} 
              onChange={(e) => setDateFrom(e.target.value)} 
              placeholder="From"
            />
            <span style={{ margin: '0 4px' }}>to</span>
            <input 
              type="date" 
              value={dateTo} 
              onChange={(e) => setDateTo(e.target.value)} 
              placeholder="To"
            />
          </div>
          <div className="results-count">
            {totalDeliveries > 0
              ? `${visibleDeliveries.length} of ${totalDeliveries} deliveries`
              : '0 deliveries'}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading deliveries...</div>
      ) : (
        <div className="admin-delivery-list">
          <div className="delivery-table-container">
            <table className="delivery-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Vendor</th>
                  <th>Delivery Partner</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Delivery Address</th>
                  <th>Items</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleDeliveries.map((delivery) => {
                  const vendorDetails = getResolvedVendorDetails(delivery);
                  const deliveryPartnerDetails = getResolvedDeliveryPartnerDetails(delivery);
                  return (
                  <tr key={delivery.id}>
                    <td style={{ fontWeight: 600 }}>{delivery.order_number}</td>
                    <td>
                      <div className="vendor-detail-cell">
                        <strong>{vendorDetails.name}</strong>
                        {vendorDetails.owner && <span>Owner: {vendorDetails.owner}</span>}
                        {vendorDetails.phone && <span>{vendorDetails.phone}</span>}
                      </div>
                    </td>
                    <td>
                      <div className="vendor-detail-cell">
                        <strong>{deliveryPartnerDetails.name}</strong>
                        {deliveryPartnerDetails.code && deliveryPartnerDetails.code !== 'N/A' && <span>Code: {deliveryPartnerDetails.code}</span>}
                        {deliveryPartnerDetails.phone && deliveryPartnerDetails.phone !== 'N/A' && <span>{deliveryPartnerDetails.phone}</span>}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <User size={14} />
                        {delivery.customer_name || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Phone size={14} />
                        {delivery.customer_phone || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: 300 }}>
                        <MapPin size={14} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {getDeliveryAddress(delivery)}
                        </span>
                      </div>
                    </td>
                    <td>{delivery.item_count || 0} items</td>
                    <td style={{ fontWeight: 600 }}>₹{formatCurrency(delivery.total_amount)}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadge(delivery.status)}`}>
                        {getStatusIcon(delivery.status)}
                        {getStatusText(delivery.status)}
                      </span>
                    </td>
                    <td>{formatDateTime(delivery.created_at)}</td>
                    <td className="actions-cell">
                      <div className="actions-container">
                        <div className="action-buttons">
                          {delivery.status === 'ready' && (
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setEditingDelivery(delivery);
                                setDeliveryAgent('');
                                setDeliveryNotes('');
                              }}
                              title="Assign Delivery"
                            >
                              <Truck size={16} />
                              Assign
                            </button>
                          )}
                          {delivery.status === 'out_for_delivery' && (
                            <button 
                              className="btn btn-success btn-sm"
                              onClick={() => updateDeliveryStatus(delivery.id, 'delivered')}
                              title="Mark as Delivered"
                            >
                              <CheckCircle size={16} />
                              Delivered
                            </button>
                          )}
                          {(delivery.status === 'ready' || delivery.status === 'out_for_delivery') && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setAssignOrder(delivery);
                                setAssignPartnerId('');
                              }}
                              title="Assign/Reassign Partner"
                            >
                              <Truck size={16} />
                              Partner
                            </button>
                          )}
                          <button 
                            className="btn btn-info btn-sm" 
                            onClick={() => {
                              setShowDetailsFor(prev => prev === delivery.id ? null : delivery.id);
                              setOrderDetails(prev => ({
                                ...prev,
                                [delivery.id]: {
                                  ...delivery,
                                  ...(prev[delivery.id] || {})
                                }
                              }));
                              if (!orderDetails[delivery.id]) fetchOrderDetails(delivery.id);
                            }}
                            title="View Details"
                          >
                            <Eye size={16} />
                            Details
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {showDetailsFor && orderDetails[showDetailsFor] && (() => {
            const rowDetails = deliveries.find(order => String(order.id) === String(showDetailsFor)) || {};
            const details = { ...rowDetails, ...orderDetails[showDetailsFor] };
            const detailVendor = getResolvedVendorDetails(details);
            const rowVendor = getResolvedVendorDetails(rowDetails);
            const detailPartner = getResolvedDeliveryPartnerDetails(details);
            const rowPartner = getResolvedDeliveryPartnerDetails(rowDetails);
            const vendorDetails = {
              name: detailVendor.name !== 'N/A' ? detailVendor.name : rowVendor.name,
              owner: firstValue(detailVendor.owner, rowVendor.owner, 'N/A'),
              phone: firstValue(detailVendor.phone, rowVendor.phone, 'N/A'),
              email: firstValue(detailVendor.email, rowVendor.email, 'N/A'),
              address: firstValue(detailVendor.address, rowVendor.address, 'N/A')
            };
            const deliveryPartnerDetails = {
              name: firstValue(detailPartner.name, rowPartner.name, 'N/A'),
              code: firstValue(detailPartner.code, rowPartner.code, 'N/A'),
              phone: firstValue(detailPartner.phone, rowPartner.phone, 'N/A')
            };
            return (
            <div className="delivery-items" style={{ marginTop: 16 }}>
              <div className="items-header">Order Details</div>
              <div className="order-info-grid">
                <div>
                  <strong>Order Number:</strong> {details.order_number}
                </div>
                <div>
                  <strong>Customer:</strong> {details.customer_name || details.customer?.name || 'N/A'}
                </div>
                <div>
                  <strong>Phone:</strong> {details.customer_phone || details.customer?.phone || 'N/A'}
                </div>
                <div>
                  <strong>Address:</strong> {getDeliveryAddress(details)}
                </div>
                <div>
                  <strong>Total Amount:</strong> {formatCurrency(details.total_amount)}
                </div>
                <div>
                  <strong>Status:</strong> {formatTitleCase(details.status)}
                </div>
                <div>
                  <strong>Vendor:</strong> {vendorDetails.name}
                </div>
                <div>
                  <strong>Delivery Partner:</strong> {deliveryPartnerDetails.name}
                </div>
                <div>
                  <strong>Vendor Owner:</strong> {vendorDetails.owner || 'N/A'}
                </div>
                <div>
                  <strong>Partner Code:</strong> {deliveryPartnerDetails.code || 'N/A'}
                </div>
                <div>
                  <strong>Vendor Phone:</strong> {vendorDetails.phone || 'N/A'}
                </div>
                <div>
                  <strong>Partner Phone:</strong> {deliveryPartnerDetails.phone || 'N/A'}
                </div>
                <div>
                  <strong>Vendor Email:</strong> {vendorDetails.email || 'N/A'}
                </div>
                <div>
                  <strong>Vendor Address:</strong> {vendorDetails.address || 'N/A'}
                </div>
              </div>
              <div className="items-header" style={{ marginTop: 16 }}>Products</div>
              {(details.items || []).map((it, index) => {
                const productDescription = getProductDescription(it);
                return (
                <div key={it.id || `${it.product_id}-${index}`} className="item-row">
                  <div className="item-name">
                    <span>{it.product_name || `#${it.product_id}`}</span>
                    {productDescription ? <small className="item-description">{productDescription}</small> : null}
                  </div>
                  <div className="item-qty">x{it.quantity}</div>
                  <div className="item-price">{formatCurrency(it.unit_price)}</div>
                  <div className="item-total">{formatCurrency(it.unit_price * it.quantity)}</div>
                </div>
                );
              })}
            </div>
            );
          })()}

          {visibleDeliveries.length === 0 && (
            <div className="empty-state">
              <Truck size={48} />
              <h3>No deliveries found</h3>
              <p>No deliveries match your current filters</p>
            </div>
          )}

          {visibleDeliveries.length > 0 && (
            <div className="pagination-container" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <div style={{ fontSize: 14, color: '#666' }}>
                Showing {visibleDeliveries.length} of {totalDeliveries} deliveries (page {currentPage} of {Math.max(totalPages, 1)})
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (currentPage > 1) {
                      const prevPage = currentPage - 1;
                      setCurrentPage(prevPage);
                      fetchDeliveries(prevPage);
                    }
                  }}
                  disabled={currentPage <= 1}
                >
                  Prev
                </button>
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`btn btn-secondary btn-sm ${page === currentPage ? 'active' : ''}`}
                      style={page === currentPage ? { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' } : {}}
                      onClick={() => {
                        if (page !== currentPage) {
                          setCurrentPage(page);
                          fetchDeliveries(page);
                        }
                      }}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (currentPage < totalPages) {
                      const nextPage = currentPage + 1;
                      setCurrentPage(nextPage);
                      fetchDeliveries(nextPage);
                    }
                  }}
                  disabled={currentPage >= totalPages || totalPages === 0}
                >
                  Next
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, color: '#666' }}>Rows per page</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    const newSize = parseInt(e.target.value, 10) || 10;
                    setRowsPerPage(newSize);
                    setCurrentPage(1);
                    fetchDeliveries(1);
                  }}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ddd' }}
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delivery Partners Management */}
      <div style={{ marginTop: 32 }}>
        <div className="admin-delivery-header" style={{ marginBottom: 16 }}>
          <div className="header-content">
            <h1>
              <Users size={28} style={{ marginRight: 12 }} />
              Delivery Partners
            </h1>
            <p>List, approve and manage all delivery partners, including online/offline status.</p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-secondary"
              onClick={() => fetchPartners(partnerPage, partnerLimit)}
              disabled={partnersLoading}
            >
              <RefreshCw size={18} />
              Refresh
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreatePartnerModal(true)}
            >
              <UserPlus size={18} />
              Add Delivery Partner
            </button>
          </div>
        </div>

        <div className="admin-delivery-filters" style={{ marginBottom: 16 }}>
          <div className="filters-left">
            <div className="search-container">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Search by name, email, phone or code"
                value={partnerSearch}
                onChange={(e) => setPartnerSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPartnerPage(1);
                    fetchPartners(1, partnerLimit);
                  }
                }}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <Filter size={20} />
              <select
                value={partnerStatusFilter}
                onChange={(e) => setPartnerStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="filter-group">
              <Filter size={20} />
              <select
                value={partnerVerificationFilter}
                onChange={(e) => setPartnerVerificationFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Verification</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
          <div className="filters-right">
            <div className="results-count">
              {partnerTotal > 0
                ? `${partners.length} of ${partnerTotal} partners`
                : '0 partners'}
            </div>
          </div>
        </div>

        <div className="admin-delivery-list">
          {partnersLoading ? (
            <div className="loading">Loading delivery partners…</div>
          ) : (
            <div className="delivery-table-container">
              <table className="delivery-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Verification</th>
                    <th>Online</th>
                    <th>Total Deliveries</th>
                    <th>Rating</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((p) => (
                    <tr key={p.id}>
                      <td>#{p.id}</td>
                      <td>{p.partner_code}</td>
                      <td>{p.full_name}</td>
                      <td>{p.email}</td>
                      <td>{p.phone}</td>
                      <td>{p.status}</td>
                      <td>{p.verification_status}</td>
                      <td>
                        {p.is_online ? (
                          <span className="status-badge status-ready">Online</span>
                        ) : (
                          <span className="status-badge status-default">Offline</span>
                        )}
                      </td>
                      <td>{p.total_deliveries ?? 0}</td>
                      <td>{p.rating ?? 0}</td>
                      <td>{formatDateTime(p.created_at)}</td>
                      <td className="actions-cell">
                        <div className="actions-container">
                          <div className="action-buttons">
                            {p.verification_status === 'pending' && (
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => performPartnerAction(p.id, 'approve')}
                                disabled={partnerActionLoadingId === p.id}
                                title="Approve Partner"
                              >
                                <CheckCircle size={14} />
                                Approve
                              </button>
                            )}
                            {p.verification_status === 'pending' && (
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => performPartnerAction(p.id, 'reject')}
                                disabled={partnerActionLoadingId === p.id}
                                title="Reject Partner"
                              >
                                <XCircle size={14} />
                                Reject
                              </button>
                            )}
                            {p.status !== 'suspended' && (
                              <button
                                className="btn btn-warning btn-sm"
                                onClick={() => performPartnerAction(p.id, 'block')}
                                disabled={partnerActionLoadingId === p.id}
                                title="Block Partner"
                              >
                                <AlertTriangle size={14} />
                                Block
                              </button>
                            )}
                            {p.status === 'suspended' && (
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => performPartnerAction(p.id, 'unblock')}
                                disabled={partnerActionLoadingId === p.id}
                                title="Unblock Partner"
                              >
                                <CheckCircle size={14} />
                                Unblock
                              </button>
                            )}
                            <button
                              className="btn btn-info btn-sm"
                              onClick={() => fetchPartnerDetails(p)}
                              disabled={partnerActionLoadingId === p.id || (partnerDetailsLoading && selectedPartnerDetails?.partner?.id === p.id)}
                              title="View Partner Details"
                            >
                              <Eye size={14} />
                              Details
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                const confirmReset = window.confirm(
                                  'Reset password for this delivery partner? A temporary password will be generated.'
                                );
                                if (confirmReset) {
                                  performPartnerAction(p.id, 'resetPassword');
                                }
                              }}
                              disabled={partnerActionLoadingId === p.id}
                              title="Reset Password"
                            >
                              <Key size={14} />
                              Reset
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!partnersLoading && partners.length === 0 && (
                    <tr>
                      <td colSpan={12} style={{ padding: 24, textAlign: 'center' }}>
                        No delivery partners found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {partners.length > 0 && (
            <div className="pagination-container">
              <div style={{ fontSize: 14, color: '#666' }}>
                Showing {partners.length} of {partnerTotal} partners (page {partnerPage} of{' '}
                {Math.max(partnerTotalPages, 1)})
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (partnerPage > 1) {
                      const prev = partnerPage - 1;
                      setPartnerPage(prev);
                      fetchPartners(prev, partnerLimit);
                    }
                  }}
                  disabled={partnerPage <= 1 || partnersLoading}
                >
                  Prev
                </button>
                {Array.from({ length: Math.min(7, partnerTotalPages || 1) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`btn btn-secondary btn-sm ${page === partnerPage ? 'active' : ''}`}
                      style={
                        page === partnerPage
                          ? { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' }
                          : {}
                      }
                      onClick={() => {
                        if (page !== partnerPage) {
                          setPartnerPage(page);
                          fetchPartners(page, partnerLimit);
                        }
                      }}
                      disabled={partnersLoading}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (partnerPage < partnerTotalPages) {
                      const next = partnerPage + 1;
                      setPartnerPage(next);
                      fetchPartners(next, partnerLimit);
                    }
                  }}
                  disabled={partnerPage >= partnerTotalPages || partnerTotalPages === 0 || partnersLoading}
                >
                  Next
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, color: '#666' }}>Rows per page</span>
                <select
                  value={partnerLimit}
                  onChange={(e) => {
                    const newSize = parseInt(e.target.value, 10) || 10;
                    setPartnerLimit(newSize);
                    setPartnerPage(1);
                    fetchPartners(1, newSize);
                  }}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ddd' }}
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Delivery Partner Modal */}
      {showCreatePartnerModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Add Delivery Partner</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowCreatePartnerModal(false);
                }}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  className="form-input"
                  name="full_name"
                  value={createPartnerForm.full_name}
                  onChange={handleCreatePartnerChange}
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  className="form-input"
                  name="email"
                  value={createPartnerForm.email}
                  onChange={handleCreatePartnerChange}
                />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input
                  className="form-input"
                  name="phone"
                  value={createPartnerForm.phone}
                  onChange={handleCreatePartnerChange}
                />
              </div>
              <div className="form-group">
                <label>Password (optional)</label>
                <input
                  className="form-input"
                  type="password"
                  name="password"
                  value={createPartnerForm.password}
                  onChange={handleCreatePartnerChange}
                />
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Leave blank to auto-generate a strong temporary password.
                </p>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCreatePartnerModal(false)}
                disabled={creatingPartner}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreatePartner}
                disabled={creatingPartner}
              >
                {creatingPartner ? 'Creating…' : 'Create Partner'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedPartnerDetails && (() => {
        const partner = selectedPartnerDetails.partner || {};
        const details = selectedPartnerDetails.details || {};
        const metrics = getPartnerMetricSummary(partner, details);
        const completedPackageOrders = Array.isArray(details.completed_package_orders) ? details.completed_package_orders : [];
        const completedDeliveryOrders = Array.isArray(details.completed_delivery_orders) ? details.completed_delivery_orders : [];
        const customerRatings = Array.isArray(details.customer_ratings) ? details.customer_ratings : [];
        const ratingSummary = details.rating_summary || {};
        const recentCompletedDeliveries = Array.isArray(details.recent_deliveries)
          ? buildRecentCompletedDeliveryFallback(details.recent_deliveries)
          : [];
        const deliveryOrdersForDisplay = completedDeliveryOrders.length > 0
          ? completedDeliveryOrders
          : recentCompletedDeliveries;
        const resolvedPartner = {
          ...partner,
          ...details,
          full_name: firstValue(details.full_name, details.name, partner.full_name, partner.name, 'N/A'),
          partner_code: firstValue(details.partner_code, partner.partner_code, 'N/A'),
          email: firstValue(details.email, partner.email, 'N/A'),
          phone: firstValue(details.phone, partner.phone, 'N/A'),
          status: firstValue(details.status, partner.status, 'N/A'),
          verification_status: firstValue(details.verification_status, partner.verification_status, 'N/A'),
          created_at: firstValue(details.created_at, partner.created_at, '')
        };
        const averageRating = parseNumberSafe(ratingSummary.average_rating, resolvedPartner.rating, 0);
        const totalUserRatings = ratingSummary.total_ratings !== undefined
          ? parseNumberSafe(ratingSummary.total_ratings, 0)
          : parseNumberSafe(customerRatings.length, resolvedPartner.total_ratings, 0);

        return (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>Delivery Partner Details</h2>
                <button
                  className="close-btn"
                  onClick={() => {
                    setSelectedPartnerDetails(null);
                    setPartnerDetailsLoading(false);
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div className="partner-summary-grid">
                  <div>
                    <strong>Name:</strong> {resolvedPartner.full_name}
                  </div>
                  <div>
                    <strong>Code:</strong> {resolvedPartner.partner_code}
                  </div>
                  <div>
                    <strong>Email:</strong> {resolvedPartner.email}
                  </div>
                  <div>
                    <strong>Phone:</strong> {resolvedPartner.phone}
                  </div>
                  <div>
                    <strong>Status:</strong> {formatTitleCase(resolvedPartner.status)}
                  </div>
                  <div>
                    <strong>Verification:</strong> {formatTitleCase(resolvedPartner.verification_status)}
                  </div>
                  <div>
                    <strong>Joined:</strong> {resolvedPartner.created_at ? formatDateTime(resolvedPartner.created_at) : 'N/A'}
                  </div>
                  <div>
                    <strong>Online:</strong> {resolvedPartner.is_online ? 'Yes' : 'No'}
                  </div>
                </div>

                <div className="partner-metrics-grid">
                  <div className="partner-metric-card">
                    <div className="partner-metric-label">
                      <Package size={16} />
                      Completed Packages
                    </div>
                    <div className="partner-metric-value">{metrics.completedPackages}</div>
                  </div>
                  <div className="partner-metric-card">
                    <div className="partner-metric-label">
                      <Truck size={16} />
                      Completed Orders
                    </div>
                    <div className="partner-metric-value">{metrics.completedDeliveries}</div>
                  </div>
                  <div className="partner-metric-card">
                    <div className="partner-metric-label">
                      <Star size={16} />
                      Average Rating
                    </div>
                    <div className="partner-metric-value">{averageRating.toFixed(1)}</div>
                  </div>
                  <div className="partner-metric-card">
                    <div className="partner-metric-label">
                      <Users size={16} />
                      User Ratings
                    </div>
                    <div className="partner-metric-value">{totalUserRatings}</div>
                  </div>
                </div>

                <div className="partner-record-sections">
                  <div className="partner-record-section partner-record-section--ratings">
                    <div className="items-header partner-record-section-heading partner-record-section-heading--ratings">
                      <Star size={18} />
                      <span>User Ratings</span>
                    </div>
                    {customerRatings.length > 0 ? (
                      <div className="partner-record-list">
                        {customerRatings.map((item, index) => {
                          const ratingValue = parseNumberSafe(item?.rating, 0);
                          const referenceCode = firstValue(
                            item?.order_number,
                            item?.reference_code,
                            item?.delivery_code,
                            item?.source_order_id,
                            `Rating ${index + 1}`
                          );
                          const customerName = firstValue(item?.customer_name, item?.full_name, item?.name, 'N/A');
                          const customerPhone = firstValue(item?.customer_phone, item?.phone, 'N/A');
                          const customerEmail = firstValue(item?.customer_email, item?.email, '');
                          const ratedAt = firstValue(item?.rated_at, item?.delivered_at, item?.updated_at, item?.created_at, '');
                          const ratingType = item?.rating_type === 'package' ? 'Package Order' : 'Product Order';

                          return (
                            <div key={`rating-${item?.rating_type || 'order'}-${referenceCode}-${index}`} className="partner-record-card">
                              <div className="partner-rating-title">
                                <span className="partner-rating-stars">{renderRatingStars(ratingValue)}</span>
                                <span className="partner-rating-score">{ratingValue.toFixed(1)}/5</span>
                              </div>
                              <div className="partner-record-meta">
                                <span>User: {customerName}</span>
                                <span>Phone: {customerPhone}</span>
                                {customerEmail ? <span>Email: {customerEmail}</span> : null}
                                <span>Order: {referenceCode}</span>
                                <span>Type: {ratingType}</span>
                                <span>Rated: {ratedAt ? formatDateTime(ratedAt) : 'N/A'}</span>
                                {item?.feedback ? <span className="partner-rating-feedback">Feedback: {item.feedback}</span> : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="partner-record-empty">No user ratings found for this partner.</div>
                    )}
                  </div>

                  <div className="partner-record-section">
                    <div className="items-header">Completed Package Orders</div>
                    {completedPackageOrders.length > 0 ? (
                      <div className="partner-record-list">
                        {completedPackageOrders.map((item, index) => {
                          const packageId = firstValue(
                            item?.package_id,
                            item?.delivery_code,
                            item?.tracking_code,
                            item?.order_number,
                            item?.id,
                            `Package ${index + 1}`
                          );
                          const customerName = firstValue(item?.customer_name, item?.full_name, item?.receiver_name, 'N/A');
                          const customerPhone = firstValue(item?.customer_phone, item?.phone, item?.receiver_phone, 'N/A');
                          const deliveryAddress = firstValue(
                            item?.delivery_address,
                            item?.drop_address,
                            item?.dropoff_address,
                            item?.destination_address,
                            item?.dropAddress,
                            'N/A'
                          );
                          const totalAmount = Number(item?.total_amount || item?.amount || item?.order_amount || item?.delivery_charge || 0);
                          const deliveredAt = firstValue(item?.delivered_at, item?.updated_at, item?.created_at, '');
                          return (
                            <div key={`package-${packageId}-${index}`} className="partner-record-card">
                              <div className="partner-record-title">{packageId}</div>
                              <div className="partner-record-meta">
                                <span>Customer: {customerName}</span>
                                <span>Phone: {customerPhone}</span>
                                <span>Address: {deliveryAddress}</span>
                                <span>Amount: {formatCurrency(totalAmount)}</span>
                                <span>Delivered: {deliveredAt ? formatDateTime(deliveredAt) : 'N/A'}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="partner-record-empty">No completed package orders found for this partner.</div>
                    )}
                  </div>

                  <div className="partner-record-section">
                    <div className="items-header partner-record-section-heading partner-record-section-heading--completed">
                      <CheckCircle size={18} />
                      <span>Completed Orders</span>
                    </div>
                    {deliveryOrdersForDisplay.length > 0 ? (
                      <div className="partner-record-list">
                        {deliveryOrdersForDisplay.map((item, index) => {
                          const orderNumber = firstValue(item?.order_number, item?.delivery_code, item?.tracking_code, item?.id, `Order ${index + 1}`);
                          const customerName = firstValue(item?.customer_name, item?.full_name, item?.customer?.name, 'N/A');
                          const customerPhone = firstValue(item?.customer_phone, item?.phone, item?.customer?.phone, 'N/A');
                          const deliveryAddress = getDeliveryAddress(item);
                          const totalAmount = Number(item?.total_amount || item?.amount || 0);
                          const deliveredAt = firstValue(item?.delivered_at, item?.updated_at, item?.created_at, '');
                          return (
                            <div key={`delivery-${orderNumber}-${index}`} className="partner-record-card">
                              <div className="partner-record-title">{orderNumber}</div>
                              <div className="partner-record-meta">
                                <span>Customer: {customerName}</span>
                                <span>Phone: {customerPhone}</span>
                                <span>Address: {deliveryAddress}</span>
                                <span>Amount: {formatCurrency(totalAmount)}</span>
                                <span>Delivered: {deliveredAt ? formatDateTime(deliveredAt) : 'N/A'}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="partner-record-empty">No completed orders found for this partner.</div>
                    )}
                  </div>
                </div>

                {partnerDetailsLoading && (
                  <p style={{ marginTop: 16 }}>Refreshing partner details...</p>
                )}
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setSelectedPartnerDetails(null);
                    setPartnerDetailsLoading(false);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

       {/* Order → Delivery Partner Assignment Modal */}
       {assignOrder && (
         <div className="modal-overlay">
           <div className="modal">
             <div className="modal-header">
               <h2>{assignOrder.status === 'ready' ? 'Assign Delivery Partner' : 'Reassign Delivery Partner'}</h2>
               <button
                 className="close-btn"
                 onClick={() => {
                   setAssignOrder(null);
                   setAssignPartnerId('');
                 }}
               >
                 <X size={20} />
               </button>
             </div>
             <div className="modal-body">
               <p><strong>Order:</strong> {assignOrder.order_number}</p>
               <p><strong>Customer:</strong> {assignOrder.customer_name}</p>
               <p><strong>Address:</strong> {assignOrder.shipping_address}</p>
               <p><strong>Amount:</strong> {formatCurrency(assignOrder.total_amount)}</p>

               <div className="form-group">
                 <label>Select Delivery Partner *</label>
                 <select
                   className="form-input"
                   value={assignPartnerId}
                   onChange={(e) => setAssignPartnerId(e.target.value)}
                 >
                   <option value="">Select partner</option>
                   {assignablePartners.map((p) => (
                     <option key={p.id} value={p.id}>
                       {p.full_name} ({p.partner_code}){p.is_online ? ' • Online' : ''}
                     </option>
                   ))}
                 </select>
                 {assignablePartners.length === 0 && (
                   <p style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>
                     No active & verified delivery partners available. Approve and activate partners first.
                   </p>
                 )}
               </div>
             </div>
             <div className="modal-actions">
               <button
                 type="button"
                 className="btn btn-secondary"
                 onClick={() => {
                   setAssignOrder(null);
                   setAssignPartnerId('');
                 }}
                 disabled={assignSaving}
               >
                 Cancel
               </button>
               <button
                 type="button"
                 className="btn btn-primary"
                 onClick={handleSaveOrderAssignment}
                 disabled={assignSaving || !assignPartnerId}
               >
                 {assignSaving ? 'Saving…' : (assignOrder.status === 'ready' ? 'Assign Partner' : 'Reassign Partner')}
               </button>
             </div>
           </div>
         </div>
       )}

      {partnerToast.visible && (
        <div
          className={`admin-toast ${partnerToast.type === 'error' ? 'admin-toast-error' : 'admin-toast-success'}`}
          style={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            padding: '8px 12px',
            borderRadius: 6,
            color: '#fff',
            background: partnerToast.type === 'error' ? '#dc2626' : '#10b981',
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
          }}
        >
          {partnerToast.text}
        </div>
      )}

      {/* Assign Delivery Modal */}
      {editingDelivery && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Assign Delivery</h2>
              <button className="close-btn" onClick={() => {
                setEditingDelivery(null);
                setDeliveryAgent('');
                setDeliveryNotes('');
              }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p><strong>Order:</strong> {editingDelivery.order_number}</p>
              <p><strong>Customer:</strong> {editingDelivery.customer_name}</p>
              <p><strong>Address:</strong> {editingDelivery.shipping_address}</p>
              <p><strong>Amount:</strong> {formatCurrency(editingDelivery.total_amount)}</p>
              
              <div className="form-group">
                <label>Delivery Agent Name *</label>
                <input
                  type="text"
                  value={deliveryAgent}
                  onChange={(e) => setDeliveryAgent(e.target.value)}
                  placeholder="Enter delivery agent name"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Delivery Notes</label>
                <textarea
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  rows="3"
                  placeholder="Add any special delivery instructions..."
                  className="form-textarea"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setEditingDelivery(null);
                  setDeliveryAgent('');
                  setDeliveryNotes('');
                }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={() => handleAssignDelivery(editingDelivery)}
                disabled={!deliveryAgent.trim()}
              >
                <Truck size={16} />
                Assign & Dispatch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDeliveryManagement;

